import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Activity } from '../entity/activity';
import { ActivityState } from '../entity/activitystate';

import { environment } from 'src/environments/environment';
import { hashCode } from '../../shared/misc/hashcode';
import { ActivityComposite } from '../entity/activity.composite';
import { Task } from '../entity/task';
import { HttpAuthorizationHeader } from '../misc/httpauthorizationheader';
import { withFirstTeamIds } from '../misc/rxjs-operators';
import { ToastActions } from '../state/toast/toast.actions';
import { toActivtyStateTypeTransformer } from '../state/transformers/transformers';
import { ActivityStateType } from '../state/types';
import { BLService } from './bl.service';
import { TaskService } from './task.service';

interface GetActivitesByUser {
  fromDate: string;
  toDate: string;
  taskUserId: number;
  clientUserId: number;
  includeArchivedClients: boolean;
}

@Injectable({ providedIn: 'root' })
export class ActivityService extends BLService {
  private options = new HttpAuthorizationHeader('application/json');
  private RESOURCE_ENDPOINT = 'activity';
  private url = environment.serverUrlV2 + this.RESOURCE_ENDPOINT;

  constructor(private taskService: TaskService) {
    super();
  }

  getActivityStates(): Observable<ActivityState[]> {
    const headers = this.options.getAuthorizationHeaderWithEmptyBody().headers;
    return this.http
      .get<ActivityState[]>(this.url + '/states', {
        headers: headers,
      })
      .pipe(this.catchErrorAndShowMessage());
  }

  getActivityStatesTypes(): Observable<ActivityStateType[]> {
    return this.getActivityStates().pipe(map((states) => states.map(toActivtyStateTypeTransformer.transform)));
  }

  getActivites(
    fromDate: string,
    toDate: string,
    taskId: number,
    includeStageChanges: boolean = false,
  ): Observable<Activity[]> {
    const urlExtension = '/task/' + taskId + '/range/' + fromDate + '/' + toDate;
    const queryParam = '?include-state-changes=' + includeStageChanges;
    return this.http
      .get<Activity[]>(`${this.url}${urlExtension}${queryParam}`, this.options.getAuthorizationHeaderWithEmptyBody())
      .pipe(
        map((activites: any[]) => activites.map((activity) => Activity.from(activity))),
        this.catchErrorAndShowMessage(),
      );
  }

  getActivity(date: string, taskId: number): Observable<Activity> {
    const urlExtension = '/task/' + taskId + '/date/' + date;
    return this.http
      .get<Activity>(this.url + urlExtension, this.options.getAuthorizationHeaderWithEmptyBody())
      .pipe(this.catchErrorAndShowMessage());
  }

  getEarliestUnfinishedDeadline(userId: number): Observable<any> {
    const urlExtension = '/earliest-unfinished-deadline/' + userId;
    return this.http
      .get<any>(this.url + urlExtension, this.options.getAuthorizationHeaderWithEmptyBody())
      .pipe(this.catchErrorAndShowMessage());
  }

  getActivitesByUser({
    fromDate,
    toDate,
    taskUserId,
    clientUserId,
    includeArchivedClients,
  }: GetActivitesByUser): Observable<Activity[]> {
    const queryParams = `include-archived-clients=${Number(includeArchivedClients)}`;
    const url = `${this.url}/by-user/${fromDate}/${toDate}/${taskUserId}/${clientUserId}?${queryParams}`;
    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.http.get<Activity[]>(url, this.options.getAuthorizationHeaderWithEmptyBody(ids)).pipe(
          map((response) => {
            if (response && response.length > 0) {
              return response.map((activity) => {
                return activity.id ? activity : { ...activity, unsavedId: hashCode(JSON.stringify(activity)) };
              });
            }
            return response;
          }),
          this.catchErrorAndShowMessage(),
        ),
      ),
    );
  }

  getActivityComposite({ span, taskId }: Activity) {
    return this.getActivity(span.end, taskId).pipe(
      tap((activity: Activity | null) => {
        if (!activity) {
          throw new Error('Activity not found');
        }
      }),
      switchMap((activity: Activity) =>
        this.taskService.getTask(activity.taskId).pipe(map((task: Task) => [activity, task])),
      ),
      map(([activity, task]: [Activity, Task]) => {
        activity.task = task;
        return new ActivityComposite(task, activity);
      }),
      catchError(() => {
        this.store.dispatch(
          ToastActions.showWarnMessage({
            summary: 'Saknas',
            detail: `Aktiviteten kan inte hittas eller ett fel uppstod (taskId: ${taskId} med slutdatum ${span.end})`,
          }),
        );
        return EMPTY;
      }),
    );
  }

  saveActivity(activity: Activity): Observable<Activity> {
    const body = JSON.stringify(activity);
    const alternativeUrl = activity.statusChangeDate ? `/changed-on/${activity.statusChangeDate}` : '';
    return this.http
      .put<Activity>(this.url + alternativeUrl, body, this.options.getAuthorizationHeader())
      .pipe(this.catchErrorAndShowMessage());
  }

  saveSingleActivity(date: string, taskId: number, comment: string): Observable<Activity> {
    return this.http
      .put<Activity>(
        `${this.url}/single-event/${date}/${taskId}?comment=${comment}`,
        null,
        this.options.getAuthorizationHeader(),
      )
      .pipe(this.catchErrorAndShowMessage());
  }

  deleteActivity(activity: Activity) {
    return this.http.delete(`${this.url}/${activity.id}`, this.options.getAuthorizationHeader()).pipe(
      tap(() =>
        this.store.dispatch(
          ToastActions.showInfoMessage({ summary: 'Borttagen', detail: 'Aktiviteten har tagits bort' }),
        ),
      ),
      this.catchErrorAndShowMessage(),
    );
  }
}
